import { pxToRem } from '@/shared/styles/themes'
import { Button, styled } from '@mui/material'
import { alpha, Theme } from '@mui/material/styles'

import { TagSize, TagType } from './Tag'

const tagSizeMap = {
  mini: {
    padding: `${pxToRem(2)} ${pxToRem(5)} ${pxToRem(2)} ${pxToRem(5)}`,
    borderRadius: 20,
    fontSize: 12,
    iconSize: 13,
    iconMargin: 2,
  },
  small: {
    padding: `${pxToRem(4)} ${pxToRem(8)} ${pxToRem(3)} ${pxToRem(8)}`,
    borderRadius: 20,
    fontSize: 13,
    iconSize: 14,
    iconMargin: 2,
  },
  medium: {
    padding: `${pxToRem(6)} ${pxToRem(10)} ${pxToRem(4)} ${pxToRem(10)}`,
    borderRadius: 18,
    fontSize: 13,
    iconSize: 16,
    iconMargin: 3,
  },
  large: {
    padding: `${pxToRem(10)} ${pxToRem(14)} ${pxToRem(8)} ${pxToRem(14)}`,
    borderRadius: 24,
    fontSize: 14,
    iconSize: 18,
    iconMargin: 4,
  },
} as const

export const getTagStyles = (type: TagType, theme: Theme) => {
  const styles = {
    neutral: {
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      hoverBg: alpha(theme.palette.text.primary, 0.1),
    },
    success: {
      color: theme.palette.success.main,
      backgroundColor: alpha(theme.palette.success.main, 0.08),
      border: 'none',
      hoverBg: alpha(theme.palette.success.main, 0.16),
    },
    warning: {
      color: theme.palette.warning.main,
      backgroundColor: alpha(theme.palette.warning.main, 0.08),
      border: 'none',
      hoverBg: alpha(theme.palette.warning.main, 0.16),
    },
    error: {
      color: theme.palette.error.main,
      backgroundColor: alpha(theme.palette.error.main, 0.08),
      border: 'none',
      hoverBg: alpha(theme.palette.error.main, 0.16),
    },
    info: {
      color: theme.palette.info.main,
      backgroundColor: alpha(theme.palette.info.main, 0.08),
      border: 'none',
      hoverBg: alpha(theme.palette.info.main, 0.16),
    },
    special: {
      color: theme.palette.secondary.main,
      backgroundColor: alpha(theme.palette.secondary.main, 0.08),
      border: `1px solid ${theme.palette.divider}`,
      hoverBg: alpha(theme.palette.secondary.main, 0.16),
    },
    transparent: {
      color: theme.palette.text.primary,
      backgroundColor: 'transparent',
      border: `1px solid ${theme.palette.divider}`,
      hoverBg: alpha(theme.palette.text.primary, 0.1),
    },
    'glass-blur': {
      color: theme.palette.common.white,
      backgroundColor: alpha(theme.palette.common.white, 0.15),
      border: `1px solid ${alpha(theme.palette.common.white, 0.25)}`,
      hoverBg: alpha(theme.palette.common.white, 0.25),
    },
    primary: {
      color: theme.palette.secondary.main,
      backgroundColor: theme.palette.primary.main,
      border: 'none',
      hoverBg: alpha(theme.palette.primary.main, 0.85),
    },
    accent: {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.secondary.main,
      border: 'none',
      hoverBg: alpha(theme.palette.secondary.main, 0.85),
    },
  }

  return styles[type]
}

export const StyledTag = styled(Button, {
  shouldForwardProp: (prop) => prop !== '$type' && prop !== '$size' && prop !== '$glassEffect' && prop !== '$disabled',
}) <{ $type: TagType; $size: TagSize; $glassEffect?: boolean; $disabled?: boolean }>`
  font-family: ${({ theme }) => theme.typography.fontFamily};
  display: inline-flex;
  align-items: center;
  font-weight: 400;
  text-decoration: none;
  transition: all 200ms ease-in-out;
  text-transform: none;
  
  ${({ theme, $type, $glassEffect, $disabled, $size = 'medium' }) => {
    const styles = getTagStyles($type, theme)
    const sizeStyles = tagSizeMap[$size]

    return `
      color: ${styles.color};
      background-color: ${styles.backgroundColor};
      border: ${styles.border};
      padding: ${sizeStyles.padding};
      border-radius: ${pxToRem(sizeStyles.borderRadius)};
      font-size: ${pxToRem(sizeStyles.fontSize)};
      ${$glassEffect ? 'backdrop-filter: blur(9px);' : ''}

      &:hover {
        background-color: ${$disabled ? styles.backgroundColor : styles.hoverBg};
        text-decoration: none;
      }
      
      & .MuiSvgIcon-root {
        font-size: ${pxToRem(sizeStyles.iconSize)};
        margin-right: ${pxToRem(sizeStyles.iconMargin)};
      }
    `
  }}
`